import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Order = Tables<'orders'>
type ActivityLog = Tables<'activity_log'>
type OrgLookup = { id: string; name: string }

const PIPELINE_STATUSES = ['received', 'validated', 'acknowledged', 'in_production', 'ready_to_ship', 'shipped', 'delivered'] as const
const PIPELINE_COLORS: Record<string, string> = {
  received: 'var(--color-primary)',
  validated: '#6366f1',
  acknowledged: '#8b5cf6',
  in_production: 'var(--color-technical)',
  ready_to_ship: '#f59e0b',
  shipped: '#3b82f6',
  delivered: 'var(--color-success)',
}

export function AnalyticsDashboardPage() {
  const [orders, setOrders] = useState<Order[]>([])
  const [pendingApps, setPendingApps] = useState(0)
  const [orgCount, setOrgCount] = useState(0)
  const [activity, setActivity] = useState<ActivityLog[]>([])
  const [orgs, setOrgs] = useState<OrgLookup[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const [ordersRes, appsRes, orgsCountRes, orgsRes, activityRes] = await Promise.all([
        supabase.from('orders').select('id, status, total, organization_id, created_at'),
        supabase.from('trade_applications').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('organizations').select('id', { count: 'exact', head: true }),
        supabase.from('organizations').select('id, name'),
        supabase.from('activity_log').select('*').order('created_at', { ascending: false }).limit(10),
      ])
      setOrders((ordersRes.data as Order[]) || [])
      setPendingApps(appsRes.count || 0)
      setOrgCount(orgsCountRes.count || 0)
      setOrgs(orgsRes.data || [])
      setActivity(activityRes.data || [])
      setLoading(false)
    }
    load()
  }, [])

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>Loading dashboard...</p>

  const activeOrders = orders.filter(o => !['delivered', 'cancelled'].includes(o.status))
  const totalRevenue = orders.reduce((sum, o) => sum + Number(o.total), 0)

  // Pipeline counts
  const pipeline: Record<string, number> = {}
  for (const s of PIPELINE_STATUSES) pipeline[s] = 0
  for (const o of orders) {
    if (pipeline[o.status] !== undefined) pipeline[o.status]++
  }
  const maxPipeline = Math.max(...Object.values(pipeline), 1)

  // Top distributors by revenue
  const revenueByOrg: Record<string, number> = {}
  for (const o of orders) {
    revenueByOrg[o.organization_id] = (revenueByOrg[o.organization_id] || 0) + Number(o.total)
  }
  const orgMap = new Map(orgs.map(o => [o.id, o.name]))
  const topDistributors = Object.entries(revenueByOrg)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([orgId, revenue]) => ({ name: orgMap.get(orgId) || 'Unknown', revenue }))

  return (
    <div>
      <PageHeader title="Dashboard" description="Platform overview and key metrics" />

      {/* Row 1: Key Metrics */}
      <div className={css.grid4} style={{ marginBottom: 'var(--space-6)' }}>
        <div className={css.widget}>
          <div className={css.widgetTitle}>Active Orders</div>
          <div className={css.widgetValue}>{activeOrders.length}</div>
        </div>
        <div className={css.widget}>
          <div className={css.widgetTitle}>Total Revenue</div>
          <div className={css.widgetValue}>£{totalRevenue.toLocaleString()}</div>
        </div>
        <div className={css.widget}>
          <div className={css.widgetTitle}>Pending Applications</div>
          <div className={css.widgetValue} style={pendingApps > 0 ? { color: 'var(--color-technical)' } : undefined}>
            {pendingApps}
          </div>
        </div>
        <div className={css.widget}>
          <div className={css.widgetTitle}>Organizations</div>
          <div className={css.widgetValue}>{orgCount}</div>
        </div>
      </div>

      {/* Row 2: Order Pipeline */}
      <div className={css.card} style={{ marginBottom: 'var(--space-6)' }}>
        <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: 'var(--space-5)' }}>Order Pipeline</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {PIPELINE_STATUSES.map(status => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <span style={{
                width: 130,
                fontSize: 'var(--text-xs)',
                fontWeight: 500,
                color: 'var(--color-text-secondary)',
                textTransform: 'capitalize',
                flexShrink: 0,
              }}>
                {status.replace(/_/g, ' ')}
              </span>
              <div style={{ flex: 1, background: 'var(--color-bg-subtle)', borderRadius: 'var(--radius-md)', height: 24, overflow: 'hidden' }}>
                <div style={{
                  width: `${(pipeline[status] / maxPipeline) * 100}%`,
                  minWidth: pipeline[status] > 0 ? 24 : 0,
                  height: '100%',
                  background: PIPELINE_COLORS[status],
                  borderRadius: 'var(--radius-md)',
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <span style={{
                width: 32,
                textAlign: 'right',
                fontSize: 'var(--text-sm)',
                fontWeight: 600,
                fontFamily: 'var(--font-mono)',
                color: 'var(--color-text)',
              }}>
                {pipeline[status]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Row 3: Activity + Top Distributors */}
      <div className={css.grid2}>
        {/* Recent Activity */}
        <div className={css.card}>
          <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>Recent Activity</h3>
          {activity.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>No recent activity</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {activity.map(a => (
                <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 'var(--text-sm)', lineHeight: 1.4 }}>{a.summary}</p>
                    <StatusBadge label={a.activity_type.replace(/_/g, ' ')} />
                  </div>
                  <span style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-muted)',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}>
                    {new Date(a.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Distributors */}
        <div className={css.card}>
          <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>Top Distributors</h3>
          {topDistributors.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>No order data yet</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {topDistributors.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <span style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: 'var(--color-primary-muted)',
                      color: 'var(--color-primary-dark)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 700,
                    }}>
                      {i + 1}
                    </span>
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>{d.name}</span>
                  </div>
                  <span style={{ fontWeight: 600, fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>
                    £{d.revenue.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
